import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { tourSteps, TourStep, getStepIndex, getTotalSteps } from '../config/tourSteps';
import { isPrimeiroAcesso, markPrimeiroAcessoCompleto } from '../services/profileService';

const TOUR_STORAGE_KEY = 'scaliz-tour-status';

interface TourStatus {
    completed: boolean;
    currentStepId: string | null;
    skipped: boolean;
}

export const useTour = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isActive, setIsActive] = useState(false);
    const [currentStepId, setCurrentStepId] = useState<string | null>(null);
    const [tourStatus, setTourStatus] = useState<TourStatus>({
        completed: false,
        currentStepId: null,
        skipped: false,
    });
    const [primeiroAcesso, setPrimeiroAcesso] = useState<boolean | null>(null); // null = loading

    // Verifica primeiro_acesso no banco ao montar
    useEffect(() => {
        const checkPrimeiroAcesso = async () => {
            try {
                const isPrimeiro = await isPrimeiroAcesso();
                setPrimeiroAcesso(isPrimeiro);
            } catch {
                setPrimeiroAcesso(false); // Fallback: assume que não é primeiro acesso
            }
        };

        checkPrimeiroAcesso();
    }, []);

    // Load tour status from localStorage
    useEffect(() => {
        const stored = localStorage.getItem(TOUR_STORAGE_KEY);
        if (stored) {
            try {
                const status = JSON.parse(stored) as TourStatus;
                setTourStatus(status);
            } catch {
                // Ignora erros de parse silenciosamente
            }
        }
    }, []);

    // Save tour status to localStorage
    const saveTourStatus = useCallback((status: TourStatus) => {
        setTourStatus(status);
        localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(status));
    }, []);

    // Get current step
    const currentStep: TourStep | null = currentStepId
        ? (tourSteps.find(step => step.id === currentStepId) ?? null)
        : null;

    const currentStepIndex = currentStepId ? getStepIndex(currentStepId) : -1;
    const totalSteps = getTotalSteps();

    // Start tour
    const startTour = useCallback(() => {
        const firstStep = tourSteps[0];
        setIsActive(true);
        setCurrentStepId(firstStep.id);
        saveTourStatus({
            completed: false,
            currentStepId: firstStep.id,
            skipped: false,
        });

        // Navigate to first page if not already there
        if (location.pathname !== firstStep.page) {
            navigate(firstStep.page);
        }
    }, [navigate, location.pathname, saveTourStatus]);

    // Complete tour
    const completeTour = useCallback(async () => {
        setIsActive(false);
        setCurrentStepId(null);
        saveTourStatus({
            completed: true,
            currentStepId: null,
            skipped: false,
        });

        try {
            await markPrimeiroAcessoCompleto();
            setPrimeiroAcesso(false); // Atualiza estado local
        } catch {
            // Falha silenciosa em produção
        }
    }, [saveTourStatus]);

    // Next step
    const nextStep = useCallback(() => {
        if (!currentStepId) return;

        const currentIndex = getStepIndex(currentStepId);
        const current = tourSteps[currentIndex];

        // Execute action if present
        if (current?.action) {
            if (current.action.type === 'navigate' && current.action.target) {
                navigate(current.action.target);
            }
        }

        // Move to next step
        if (currentIndex < tourSteps.length - 1) {
            const nextStepData = tourSteps[currentIndex + 1];
            setCurrentStepId(nextStepData.id);
            saveTourStatus({
                completed: false,
                currentStepId: nextStepData.id,
                skipped: false,
            });

            // Navigate if next step is on different page
            if (nextStepData.page !== current.page && !current.action) {
                navigate(nextStepData.page);
            }
        } else {
            // Tour completed
            completeTour();
        }
    }, [currentStepId, navigate, saveTourStatus, completeTour]);

    // Previous step
    const previousStep = useCallback(() => {
        if (!currentStepId) return;

        const currentIndex = getStepIndex(currentStepId);
        if (currentIndex > 0) {
            const prevStepData = tourSteps[currentIndex - 1];
            setCurrentStepId(prevStepData.id);
            saveTourStatus({
                completed: false,
                currentStepId: prevStepData.id,
                skipped: false,
            });

            // Navigate if previous step is on different page
            if (prevStepData.page !== tourSteps[currentIndex].page) {
                navigate(prevStepData.page);
            }
        }
    }, [currentStepId, navigate, saveTourStatus]);

    // Skip tour
    const skipTour = useCallback(async () => {
        setIsActive(false);
        setCurrentStepId(null);
        saveTourStatus({
            completed: false,
            currentStepId: null,
            skipped: true,
        });

        // ✅ ATUALIZA O BANCO: marca primeiro_acesso como false (mesmo ao pular)
        try {
            await markPrimeiroAcessoCompleto();
            setPrimeiroAcesso(false);
        } catch {
            // Falha silenciosa em produção
        }
    }, [saveTourStatus]);

    // Reset tour
    const restartTour = useCallback(() => {
        localStorage.removeItem(TOUR_STORAGE_KEY);
        setTourStatus({
            completed: false,
            currentStepId: null,
            skipped: false,
        });
        setIsActive(false);
        setCurrentStepId(null);
    }, []);

    // Check if should auto-start
    // ✅ AGORA USA O BANCO: só auto-inicia se primeiro_acesso === true no banco
    const shouldAutoStart = primeiroAcesso === true && !currentStepId;

    // Get tour status (exported for external use)
    const getTourStatus = useCallback(() => tourStatus, [tourStatus]);

    return {
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps,
        tourStatus,
        shouldAutoStart,
        primeiroAcesso, // ✅ Expõe o flag do banco
        startTour,
        nextStep,
        previousStep,
        skipTour,
        completeTour,
        resetTour: restartTour,  // Mantém resetTour por compatibilidade
        restartTour,             // Novo nome mais claro
        getTourStatus,           // Nova: retorna status persistido
    };
};
